/** Canonical feet geometry. x/y are the floor plane; z is elevation (not Three's y). */
export interface WallPoint { x: number; y: number; z: number }
export interface PaintedWallGeometry {
  schema: 'ft.wall-path.v1';
  points: WallPoint[];
  heightFt: number;
  thicknessFt: number;
  attachment: 'surface' | 'floating';
}
export const WALL_MAX_POINTS = 1024;
export const WALL_EPSILON = 0.001;
/** Nine cover/aim samples on the actual ribbon, invariant under path subdivision.
 * Preserve the usual three height bands; horizontal samples follow arc length,
 * not the empty chord between the ends of a bent section. */
export function paintedWallTargetSamples(wall: PaintedWallGeometry): WallPoint[] {
  const length=wallLength(wall.points),bases:WallPoint[]=[];
  for(const fraction of [.2,.5,.8]) {
    let remaining=length*fraction;
    for(let i=1;i<wall.points.length;i++) {
      const a=wall.points[i-1]!,b=wall.points[i]!,run=wallDistance(a,b);
      if(remaining<=run||i===wall.points.length-1) {
        const t=run>0?Math.max(0,Math.min(1,remaining/run)):0;
        bases.push({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t,z:a.z+(b.z-a.z)*t});break;
      }
      remaining-=run;
    }
  }
  return [.2,.5,.8].flatMap(level=>bases.map(p=>({...p,z:p.z+wall.heightFt*level})));
}
/** Wall-run combat range extended to rounded painted spans. Vertical separation
 * uses the source's rules span; only size beyond the ordinary five-foot cell
 * grants horizontal range credit. Never use a section's inventory size tier. */
export function paintedWallCombatDistance(wall: PaintedWallGeometry, source: WallPoint, sourceSizeFt: number): number {
  const size = Number.isFinite(sourceSizeFt) ? Math.max(0,sourceSizeFt) : 0, half = size/2;
  let best = Infinity;
  for (let i=1;i<wall.points.length;i++) {
    const a=wall.points[i-1]!, b=wall.points[i]!;
    const squared=(t:number):number=>{
      const x=a.x+(b.x-a.x)*t,y=a.y+(b.y-a.y)*t,z=a.z+(b.z-a.z)*t;
      const horizontal=Math.max(0,Math.hypot(source.x-x,source.y-y)-wall.thicknessFt/2);
      const vertical=Math.max(0,z-source.z-half,source.z-half-z-wall.heightFt);
      return horizontal*horizontal+vertical*vertical;
    };
    let low=0,high=1;
    for(let j=0;j<64;j++){
      const left=low+(high-low)/3,right=high-(high-low)/3;
      if(squared(left)<squared(right))high=right;else low=left;
    }
    best=Math.min(best,squared(0),squared(1),squared((low+high)/2));
  }
  return Math.max(0,Math.sqrt(best)-Math.max(0,(size-5)/2));
}
export interface WallOccupancyPart {
  kind: 'circle' | 'oblong'; x: number; y: number; zMin: number; zMax: number;
  r?: number; halfW?: number; halfD?: number; facing?: number;
}
/** Exact horizontal footprint for ONE body's vertical interval. Never reuse it
 * for another altitude. A capsule is a rectangle plus two circles; clipping the
 * centerline first preserves sloped bases and the existing occupancy solver. */
export function wallOccupancyParts(wall: PaintedWallGeometry, zMin: number, zMax: number): WallOccupancyPart[] {
  if (!Number.isFinite(zMin) || !Number.isFinite(zMax) || zMax <= zMin) return [];
  const out: WallOccupancyPart[] = [], r = wall.thicknessFt / 2;
  for (let i = 1; i < wall.points.length; i++) {
    const a = wall.points[i - 1]!, b = wall.points[i]!, dz = b.z - a.z;
    let low = 0, high = 1;
    const bottom = zMin - wall.heightFt + 1e-6, top = zMax - 1e-6;
    if (Math.abs(dz) < 1e-9) { if (a.z <= bottom || a.z >= top) continue; }
    else {
      const t1 = (bottom - a.z) / dz, t2 = (top - a.z) / dz;
      low = Math.max(0, Math.min(t1, t2)); high = Math.min(1, Math.max(t1, t2));
      if (low >= high) continue;
    }
    const x1 = a.x + (b.x - a.x) * low, y1 = a.y + (b.y - a.y) * low;
    const x2 = a.x + (b.x - a.x) * high, y2 = a.y + (b.y - a.y) * high;
    out.push({kind:'oblong', x:(x1+x2)/2, y:(y1+y2)/2, halfW:Math.hypot(x2-x1,y2-y1)/2, halfD:r, facing:Math.atan2(y2-y1,x2-x1), zMin,zMax},
      {kind:'circle',x:x1,y:y1,r,zMin,zMax},{kind:'circle',x:x2,y:y2,r,zMin,zMax});
  }
  return out;
}
export const wallDistance = (a: WallPoint, b: WallPoint): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
export function wallLength(points: readonly WallPoint[]): number {
  return points.reduce((sum, p, i) => sum + (i ? wallDistance(points[i - 1]!, p) : 0), 0);
}
export function parsePaintedWall(value: unknown): PaintedWallGeometry | null {
  if (typeof value === 'string') {
    if (value.length > 150000) return null;
    try { value = JSON.parse(value); } catch { return null; }
  }
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (row.schema !== 'ft.wall-path.v1' || !Array.isArray(row.points)
    || row.points.length < 2 || row.points.length > WALL_MAX_POINTS
    || !['surface', 'floating'].includes(String(row.attachment))) return null;
  for (const key of ['heightFt', 'thicknessFt']) {
    if (typeof row[key] !== 'number' || !Number.isFinite(row[key]) || row[key] <= 0 || row[key] > 10000) return null;
  }
  const points: WallPoint[] = [];
  for (const raw of row.points) {
    if (!raw || typeof raw !== 'object') return null;
    for (const key of ['x', 'y', 'z']) {
      if (typeof raw[key] !== 'number' || !Number.isFinite(raw[key]) || Math.abs(raw[key]) > 10000000) return null;
    }
    const p = { x: raw.x, y: raw.y, z: raw.z };
    if (points.length && wallDistance(points[points.length - 1]!, p) < WALL_EPSILON) return null;
    // v1 is a ribbon raised vertically from a path, never a zero-run vertical spike.
    if (points.length && Math.hypot(points[points.length - 1]!.x - p.x, points[points.length - 1]!.y - p.y) < WALL_EPSILON) return null;
    points.push(p);
  }
  return { schema: 'ft.wall-path.v1', points, heightFt: row.heightFt as number,
    thicknessFt: row.thicknessFt as number, attachment: row.attachment as PaintedWallGeometry['attachment'] };
}

/** Exact cylinder contact with the union of round-joined, vertically extruded spans. */
export function wallIntersectsCylinder(wall: PaintedWallGeometry, p: WallPoint, radius: number, height: number): boolean {
  for (let i = 1; i < wall.points.length; i++) {
    const a = wall.points[i - 1]!, b = wall.points[i]!;
    const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
    let low = 0, high = 1;
    const minBase = p.z - wall.heightFt - WALL_EPSILON, maxBase = p.z + height + WALL_EPSILON;
    if (Math.abs(dz) < 1e-9) {
      if (a.z < minBase || a.z > maxBase) continue;
    } else {
      const t1 = (minBase - a.z) / dz, t2 = (maxBase - a.z) / dz;
      low = Math.max(0, Math.min(t1, t2)); high = Math.min(1, Math.max(t1, t2));
      if (low > high) continue;
    }
    const t = Math.max(low, Math.min(high, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
    if (Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy) <= radius + wall.thicknessFt / 2 + WALL_EPSILON) return true;
  }
  return false;
}

/** Oriented body contact. Vertical clipping retains slopes; the horizontal
 * distance is minimized analytically on point-to-rectangle quadratic intervals. */
export function wallIntersectsOblong(wall: PaintedWallGeometry, p: WallPoint, halfW: number, halfD: number, facing: number, height: number): boolean {
  if (![p.x, p.y, p.z, halfW, halfD, facing, height].every(Number.isFinite) || halfW < 0 || halfD < 0 || height < 0) return false;
  const c = Math.cos(facing), s = Math.sin(facing), limit = (wall.thicknessFt / 2 + WALL_EPSILON) ** 2;
  for (let i = 1; i < wall.points.length; i++) {
    const a = wall.points[i - 1]!, b = wall.points[i]!, dz = b.z - a.z;
    let low = 0, high = 1;
    const minBase = p.z - wall.heightFt - WALL_EPSILON, maxBase = p.z + height + WALL_EPSILON;
    if (Math.abs(dz) < 1e-9) { if (a.z < minBase || a.z > maxBase) continue; }
    else {
      const t1 = (minBase - a.z) / dz, t2 = (maxBase - a.z) / dz;
      low = Math.max(0, Math.min(t1, t2)); high = Math.min(1, Math.max(t1, t2));
      if (low > high) continue;
    }
    const x = (a.x - p.x) * c + (a.y - p.y) * s, y = -(a.x - p.x) * s + (a.y - p.y) * c;
    const dx = (b.x - a.x) * c + (b.y - a.y) * s, dy = -(b.x - a.x) * s + (b.y - a.y) * c;
    const distance = (t: number): number => Math.max(0, Math.abs(x + dx * t) - halfW) ** 2 + Math.max(0, Math.abs(y + dy * t) - halfD) ** 2;
    const axes = [[x, dx, halfW], [y, dy, halfD]] as const, knots = [low, high];
    for (const [origin, delta, extent] of axes) {
      if (Math.abs(delta) < 1e-12) continue;
      for (const edge of [-extent, extent]) { const t = (edge - origin) / delta; if (t > low && t < high) knots.push(t); }
    }
    knots.sort((a, b) => a - b);
    if (knots.some(t => distance(t) <= limit)) return true;
    for (let j = 1; j < knots.length; j++) {
      const start = knots[j - 1]!, end = knots[j]!, middle = (start + end) / 2;
      let numerator = 0, denominator = 0;
      for (const [origin, delta, extent] of axes) {
        const at = origin + delta * middle;
        if (Math.abs(at) <= extent) continue;
        numerator += delta * (origin - Math.sign(at) * extent); denominator += delta * delta;
      }
      if (denominator > 0 && distance(Math.max(start, Math.min(end, -numerator / denominator))) <= limit) return true;
    }
  }
  return false;
}

/** Continuous moving-cylinder contact. Clip (movement t, wall u) by vertical overlap,
 * then minimize horizontal distance on that convex polygon: no stepping over thin walls. */
export function wallIntersectsSweep(wall: PaintedWallGeometry, from: WallPoint, to: WallPoint, radius: number, height: number): boolean {
  for (let i = 1; i < wall.points.length; i++) {
    const a = wall.points[i - 1]!, b = wall.points[i]!;
    const dx = to.x-from.x, dy = to.y-from.y, dz = to.z-from.z;
    const wx = b.x-a.x, wy = b.y-a.y, wz = b.z-a.z;
    const qx = from.x-a.x, qy = from.y-a.y, qz = from.z-a.z;
    let polygon = [[0,0], [1,0], [1,1], [0,1]];
    for (const [sign, bound] of [[1, wall.heightFt+WALL_EPSILON], [-1, height+WALL_EPSILON]]) {
      const output: number[][] = [];
      const distance = (p: number[]): number => sign!*(qz+dz*p[0]!-wz*p[1]!)-bound!;
      for (let j = 0; j < polygon.length; j++) {
        const p = polygon[j]!, n = polygon[(j+1)%polygon.length]!;
        const pd = distance(p), nd = distance(n);
        if (pd <= 0) output.push(p);
        if ((pd <= 0) !== (nd <= 0)) {
          const t = pd/(pd-nd); output.push([p[0]!+(n[0]!-p[0]!)*t, p[1]!+(n[1]!-p[1]!)*t]);
        }
      }
      polygon = output;
    }
    if (!polygon.length) continue;
    const determinant = dy*wx-dx*wy;
    if (Math.abs(determinant) > 1e-12) {
      const t = (qx*wy-qy*wx)/determinant, u = (qx*dy-qy*dx)/determinant;
      const z = qz+dz*t-wz*u;
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1 && z >= -height-WALL_EPSILON && z <= wall.heightFt+WALL_EPSILON) return true;
    }
    for (let j = 0; j < polygon.length; j++) {
      const p = polygon[j]!, n = polygon[(j+1)%polygon.length]!;
      const x = qx+dx*p[0]!-wx*p[1]!, y = qy+dy*p[0]!-wy*p[1]!;
      const ex = dx*(n[0]!-p[0]!)-wx*(n[1]!-p[1]!), ey = dy*(n[0]!-p[0]!)-wy*(n[1]!-p[1]!);
      const norm = ex*ex+ey*ey;
      const t = norm < 1e-18 ? 0 : Math.max(0, Math.min(1, -(x*ex+y*ey)/norm));
      if (Math.hypot(x+t*ex,y+t*ey) <= radius+wall.thicknessFt/2+WALL_EPSILON) return true;
    }
  }
  return false;
}

/** Sphere contact with the actual rounded wall prism, including sloped bases and top corners.
 * The squared distance to a span's vertical capsule cross-section is convex in its span parameter.
 * Bounded minimization avoids treating a cloud's bounding cylinder as the cloud itself. */
export function wallIntersectsSphere(wall: PaintedWallGeometry, center: WallPoint, radius: number): boolean {
  if (!Number.isFinite(radius) || radius < 0) return false;
  for (let i = 1; i < wall.points.length; i++) {
    const a = wall.points[i - 1]!, b = wall.points[i]!;
    const squaredDistance = (t: number): number => {
      const x = a.x + (b.x - a.x) * t, y = a.y + (b.y - a.y) * t, z = a.z + (b.z - a.z) * t;
      const horizontal = Math.max(0, Math.hypot(center.x - x, center.y - y) - wall.thicknessFt / 2);
      const vertical = Math.max(0, z - center.z, center.z - z - wall.heightFt);
      return horizontal * horizontal + vertical * vertical;
    };
    let low = 0, high = 1;
    for (let j = 0; j < 64; j++) {
      const left = low + (high - low) / 3, right = high - (high - low) / 3;
      if (squaredDistance(left) < squaredDistance(right)) high = right; else low = left;
    }
    if (Math.min(squaredDistance(0), squaredDistance(1), squaredDistance((low + high) / 2))
      <= (radius + WALL_EPSILON) ** 2) return true;
  }
  return false;
}

/** Freehand draft; samples are validated before mutation and never simplified through obstacles. */
export class PaintedWallBrush {
  points: WallPoint[] = [];
  private strokes: WallPoint[][] = [];
  beginStroke(): void { this.strokes.push(this.points.map(p => ({ ...p }))); }
  undo(): void { this.points = this.strokes.pop() ?? []; }
  reset(): void { this.points = []; this.strokes = []; }
  append(point: WallPoint, budget: number, legal: (point: WallPoint) => boolean): boolean {
    const last = this.points[this.points.length - 1];
    if (!last) { if (!legal(point)) return false; this.points.push({ ...point }); return true; }
    const distance = wallDistance(last, point);
    if (distance < 0.1) return true;
    // A pointer returning to the preceding sample erases the tail instead of adding duplicate paint.
    const previous = this.points[this.points.length - 2];
    if (previous && wallDistance(previous, point) < 0.1) { this.points.pop(); return true; }
    const available = Math.max(0, budget - wallLength(this.points));
    if (available < WALL_EPSILON) return false;
    const ratio = Math.min(1, available / distance);
    const end = { x: last.x + (point.x - last.x) * ratio, y: last.y + (point.y - last.y) * ratio, z: last.z + (point.z - last.z) * ratio };
    if (Math.hypot(end.x - last.x, end.y - last.y) < WALL_EPSILON) return false;
    const count = Math.max(1, Math.ceil(wallDistance(last, end) / 0.25));
    if (this.points.length + count > WALL_MAX_POINTS) return false;
    const additions: WallPoint[] = [];
    for (let i = 1; i <= count; i++) {
      const t = i / count;
      const p = { x: last.x + (end.x - last.x) * t, y: last.y + (end.y - last.y) * t, z: last.z + (end.z - last.z) * t };
      if (!legal(p)) return false;
      additions.push(p);
    }
    this.points.push(...additions);
    return true;
  }
  geometry(heightFt: number, thicknessFt: number, attachment: PaintedWallGeometry['attachment']): PaintedWallGeometry | null {
    return parsePaintedWall({ schema: 'ft.wall-path.v1', points: this.points, heightFt, thicknessFt, attachment });
  }
}
